import { Label as LabelPrimitive } from 'radix-ui'
import { useId } from 'react'
import type * as React from 'react'
import { cn } from '../lib/cn.js'

export function Label({
  className,
  required,
  ...props
}: React.ComponentProps<typeof LabelPrimitive.Root> & { required?: boolean }) {
  return (
    <LabelPrimitive.Root
      className={cn('text-sm font-medium leading-none text-foreground', className)}
      {...props}
    >
      {props.children}
      {required ? (
        <span className="ms-0.5 text-danger" aria-hidden="true">
          *
        </span>
      ) : null}
    </LabelPrimitive.Root>
  )
}

export interface FieldProps {
  label: string
  /** Wired to aria-describedby so it is announced with the input, not after it. */
  hint?: string
  error?: string
  required?: boolean
  children: (props: {
    id: string
    'aria-describedby': string | undefined
    'aria-invalid': boolean | undefined
  }) => React.ReactNode
  className?: string
}

/**
 * One field: label, control, hint and error, wired together properly.
 *
 * The association matters more than it looks — a screen reader user who tabs
 * into an invalid field should hear the label, the hint and the error without
 * having to hunt around the page for red text.
 */
export function Field({ label, hint, error, required, children, className }: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })}
      {error ? (
        <p id={errorId} className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-xs text-foreground-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
